import React, { useCallback, useEffect, useState } from "react";
import "../add-project/addProject.css";
import { UilCheck, UilImageUpload } from "@iconscout/react-unicons";
import { useLocation, useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import axios from "axios";
import { SizeChartEditor } from "./SizeChartEditor";
import ProductImageUpload, { getImageFilesForUpload } from "./ProductImageUpload";
import PixelColorPicker from "./PixelColorPicker";
import { resolveUrl } from "../../../utils/storeUtils";

const SITE_CATEGORY_OPTIONS = [
  { en: "Fit out", ar: "تجهيز" },
  { en: "Furniture", ar: "اثاث" },
  { en: "Upholstery", ar: "تنجيد" },
  { en: "Staircases", ar: "سلالم" },
  { en: "Partitions", ar: "أقسام" },
  { en: "Ceiling", ar: "السقف" },
  { en: "Doors", ar: "أبواب" },
  { en: "Moroccan Handcraft", ar: "صناعة يدوية مغربية" },
];

function flattenCategoryTree(list, depth = 0, out = []) {
  (Array.isArray(list) ? list : []).forEach((c) => {
    out.push({ ...c, __depth: depth });
    if (Array.isArray(c.children) && c.children.length > 0) {
      flattenCategoryTree(c.children, depth + 1, out);
    }
  });
  return out;
}

const EditProduct = () => {
  const [name, setName] = useState("");
  const [arName, setArName] = useState("");
  const [category, setCategory] = useState("");
  const [arCategory, setArCategory] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [productSku, setProductSku] = useState("");
  const [price, setPrice] = useState("");
  const [image, setImage] = useState(null);
  const [storeImages, setStoreImages] = useState([]);
  const [originalStoreImageUrls, setOriginalStoreImageUrls] = useState([]);
  const [storeImagePreviewUrls, setStoreImagePreviewUrls] = useState([]);
  const [enableColors, setEnableColors] = useState(false);
  const [colorMode, setColorMode] = useState("perImage"); // 'perImage' | 'single'
  const [imageColorAssignments, setImageColorAssignments] = useState([]);
  const [colorPickerState, setColorPickerState] = useState({
    open: false,
    imageUrl: "",
    imageIndex: null,
    hexSlot: 0,
  });
  const [alt, setAlt] = useState("");
  const [arAlt, setArAlt] = useState("");
  const [stockQuantity, setStockQuantity] = useState("0");
  const [minDeliveryDays, setMinDeliveryDays] = useState("5");
  const [maxDeliveryDays, setMaxDeliveryDays] = useState("7");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [description, setDescription] = useState("");
  const [arDescription, setArDescription] = useState("");
  const [sizeChart, setSizeChart] = useState(null);
  const [curtainPricing, setCurtainPricing] = useState(null);
  const [curtainSample, setCurtainSample] = useState({
    width: 100,
    height: 150,
    chain: "plastic",
    wAdj: "none",
    hAdj: "none",
  });
  const [loading, setLoading] = useState(false);
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isStoreContext = location.pathname.startsWith("/dashboard/store");
  const productsEndpoint = isStoreContext ? "/store/products" : "/products";
  const redirectPath = isStoreContext
    ? "/dashboard/store/products"
    : "/dashboard/products";
  const [storeCategories, setStoreCategories] = useState([]);
  const categoryOptions = SITE_CATEGORY_OPTIONS;
  const apiBaseUrl = axios.defaults.baseURL || "";

  const descEnRef = React.useRef(null);
  const descArRef = React.useRef(null);

  const toggleMarkdownWrap = (ref, setValue, wrapLeft, wrapRight = wrapLeft) => {
    const el = ref?.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setValue((prev) => {
      const s = String(prev || "");
      const selected = s.slice(start, end) || "";
      requestAnimationFrame(() => {
        try {
          el.focus();
        } catch (_) {}
      });
      const hasWrapBeforeSel =
        start >= wrapLeft.length && s.slice(start - wrapLeft.length, start) === wrapLeft;
      const hasWrapAfterSel = s.slice(end, end + wrapRight.length) === wrapRight;
      const canUnwrapSel = selected && hasWrapBeforeSel && hasWrapAfterSel;

      const noSel = start === end;
      const hasWrapBeforeCaret =
        noSel && start >= wrapLeft.length && s.slice(start - wrapLeft.length, start) === wrapLeft;
      const hasWrapAfterCaret = noSel && s.slice(start, start + wrapRight.length) === wrapRight;
      const canUnwrapCaret = noSel && hasWrapBeforeCaret && hasWrapAfterCaret;

      const next = canUnwrapSel
        ? `${s.slice(0, start - wrapLeft.length)}${selected}${s.slice(end + wrapRight.length)}`
        : canUnwrapCaret
        ? `${s.slice(0, start - wrapLeft.length)}${s.slice(start + wrapRight.length)}`
        : `${s.slice(0, start)}${wrapLeft}${selected}${wrapRight}${s.slice(end)}`;

      requestAnimationFrame(() => {
        try {
          el.focus();
          if (canUnwrapSel) {
            el.setSelectionRange(start - wrapLeft.length, end - wrapLeft.length);
          } else if (canUnwrapCaret) {
            el.setSelectionRange(start - wrapLeft.length, start - wrapLeft.length);
          } else if (selected) {
            el.setSelectionRange(start + wrapLeft.length, end + wrapLeft.length);
          } else {
            el.setSelectionRange(start + wrapLeft.length, start + wrapLeft.length);
          }
        } catch (_) {}
      });

      return next;
    });
  };

  const toggleMarkdownBullets = (ref, setValue) => {
    const el = ref?.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setValue((prev) => {
      const s = String(prev || "");
      const selected = s.slice(start, end) || "";
      const target = selected || s;
      const lines = target.replace(/\r\n/g, "\n").split("\n");
      const nonEmpty = lines.filter((ln) => ln.trim());
      const allBulleted =
        nonEmpty.length > 0 && nonEmpty.every((ln) => ln.trimStart().startsWith("- "));
      const nextBlock = lines
        .map((ln) => {
          if (!ln.trim()) return ln;
          const raw = ln.trimStart();
          if (allBulleted) {
            return raw.startsWith("- ") ? raw.slice(2) : raw;
          }
          return raw.startsWith("- ") ? raw : `- ${raw}`;
        })
        .join("\n");
      const next = selected
        ? `${s.slice(0, start)}${nextBlock}${s.slice(end)}`
        : nextBlock;
      requestAnimationFrame(() => {
        try {
          el.focus();
          if (selected) el.setSelectionRange(start, start + nextBlock.length);
        } catch (_) {}
      });
      return next;
    });
  };

  const selectedStoreCategoryNode = isStoreContext
    ? storeCategories.find((c) => String(c._id) === String(categoryId))
    : null;
  const isCurtainsRootSelected = React.useMemo(() => {
    if (!isStoreContext || !selectedStoreCategoryNode) return false;
    const byId = new Map(storeCategories.map((n) => [String(n._id), n]));
    let cur = selectedStoreCategoryNode;
    let guard = 0;
    while (cur?.parentId && guard < 40) {
      const next = byId.get(String(cur.parentId));
      if (!next) break;
      cur = next;
      guard += 1;
    }
    return String(cur?.slug || "").toLowerCase() === "curtains";
  }, [isStoreContext, selectedStoreCategoryNode, storeCategories]);

  useEffect(() => {
    if (!isCurtainsRootSelected) return;
    setCurtainPricing((prev) =>
      prev || {
        enabled: true,
        basePrice: 0,
        widthAddons: { 100: 0, 125: 0, 150: 0, 175: 0, 200: 0, 225: 0 },
        widthPrices: { 100: 0, 125: 0, 150: 0, 175: 0, 200: 0, 225: 0 },
        heightAddons: { 150: 0, 175: 0, 200: 0, 225: 0, 250: 0, 275: 0 },
        adjustmentAddons: { none: 0, "+5": 0, "+10": 0, "-5": 0, "-10": 0 },
        chainTypeAddons: { metal: 0, plastic: 0 },
      }
    );
  }, [isCurtainsRootSelected]);

  const curtainPricingPreview = React.useMemo(() => {
    if (!isCurtainsRootSelected || !curtainPricing) return null;
    const base = Number(curtainPricing.basePrice ?? 0) || 0;
    const w = String(curtainSample.width);
    const h = String(curtainSample.height);
    const widthAddon = Number(curtainPricing.widthAddons?.[w] ?? 0) || 0;
    const heightAddon = Number(curtainPricing.heightAddons?.[h] ?? 0) || 0;
    const chainAddon = Number(curtainPricing.chainTypeAddons?.[curtainSample.chain] ?? 0) || 0;
    const wAdjAddon = Number(curtainPricing.adjustmentAddons?.[curtainSample.wAdj] ?? 0) || 0;
    const hAdjAddon = Number(curtainPricing.adjustmentAddons?.[curtainSample.hAdj] ?? 0) || 0;
    const total = base + widthAddon + heightAddon + chainAddon + wAdjAddon + hAdjAddon;
    return { base, widthAddon, heightAddon, chainAddon, wAdjAddon, hAdjAddon, total };
  }, [isCurtainsRootSelected, curtainPricing, curtainSample]);

  useEffect(() => {
    if (!isStoreContext) return;
    axios
      .get("/store/categories/tree?includeInactive=1")
      .then(({ data }) => {
        setStoreCategories(flattenCategoryTree(data || []));
      })
      .catch(() => setStoreCategories([]));
  }, [isStoreContext]);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${productsEndpoint}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setName(data.name || "");
      setArName(data.arName || "");
      setCategory(data.category || "");
      setArCategory(data.arCategory || "");
      setCategoryId(String(data.categoryId || ""));
      setProductSku(String(data.sku || "").trim());
      setPrice(data.price ?? "");
      const legacyImage = data.image || null;
      const gallery = Array.isArray(data.images) && data.images.length > 0 ? data.images : legacyImage?.url ? [legacyImage] : [];
      if (isStoreContext) {
        const normalized = gallery.map((img) => ({
          url: img?.url || "",
          alt: img?.alt || "",
          arAlt: img?.arAlt || "",
          noBgUrl: img?.noBgUrl || "",
          objectPosition: img?.objectPosition || "center",
        })).filter((img) => String(img.url || "").trim());
        setStoreImages(normalized);
        setOriginalStoreImageUrls(normalized.map((img) => img.url));
        const first = normalized[0] || null;
        setAlt(first?.alt || "");
        setArAlt(first?.arAlt || "");

        // Initialize colors assignments (per image) from saved colors
        const colors = Array.isArray(data?.colors) ? data.colors : [];
        const nextAssignments = normalized.map((_, index) => {
          const colorEntry = colors.find((c) => Number(c?.imageIndex) === index) || null;
          const hexes =
            Array.isArray(colorEntry?.hexes) && colorEntry.hexes.length > 0
              ? colorEntry.hexes
              : colorEntry?.hex
              ? [colorEntry.hex]
              : [];
          return {
            name: colorEntry?.name || "",
            hexes: hexes.map((h) => String(h || "").trim()).filter(Boolean),
            imageIndex: index,
          };
        });
        setEnableColors(colors.length > 0);
        setColorMode(String(data?.colorMode || "").toLowerCase() === "single" ? "single" : "perImage");
        setImageColorAssignments(nextAssignments);
      } else {
        const firstStoreImage = Array.isArray(data.images) ? data.images[0] : null;
        setImage(firstStoreImage?.url || legacyImage?.url || null);
        setAlt(firstStoreImage?.alt || legacyImage?.alt || "");
        setArAlt(firstStoreImage?.arAlt || legacyImage?.arAlt || "");
      }
      if (isStoreContext) {
        setStockQuantity(String(data.stock_quantity ?? 0));
        setMinDeliveryDays(String(data.min_delivery_days ?? 5));
        setMaxDeliveryDays(String(data.max_delivery_days ?? 7));
        setDiscountPercentage(
          String(
            data.discount_percentage != null ? data.discount_percentage : 0
          )
        );
        setDescription(String(data.description ?? ""));
        setArDescription(String(data.arDescription ?? ""));
        setSizeChart(data.sizeChart || null);
        setCurtainPricing(data.curtainPricing || null);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch product data."
      );
    } finally {
      setLoading(false);
    }
  }, [id, token, productsEndpoint, isStoreContext]);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    } else {
      fetchProduct();
    }
  }, [token, navigate, fetchProduct]);

  const handleFileUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      setImage(file);
    } else {
      toast.error("Please upload a valid image file (png, jpg, jpeg).");
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
  };

  const imageKey = (img) => {
    if (!img) return "";
    if (img instanceof File) {
      return `file:${img.name}:${img.lastModified}:${img.size}`;
    }
    if (typeof img === "string") return `url:${img}`;
    return `url:${img.url || ""}`;
  };

  const handleColorHexChange = (index, slot, value) => {
    setImageColorAssignments((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const nextHexes = Array.isArray(entry.hexes) ? [...entry.hexes] : [];
        while (nextHexes.length <= slot) nextHexes.push("");
        nextHexes[slot] = value;
        return { ...entry, hexes: nextHexes, imageIndex: i };
      })
    );
  };

  const openColorPicker = (index, hexSlot = 0) => {
    const imageUrl = storeImagePreviewUrls[index];
    if (!imageUrl) return;
    setColorPickerState({ open: true, imageUrl, imageIndex: index, hexSlot });
  };

  const MAX_IMAGE_COLORS = 4;
  const addColorSlot = (index) => {
    setImageColorAssignments((prev) =>
      (Array.isArray(prev) ? prev : []).map((entry, i) => {
        if (i !== index) return entry;
        const nextHexes = Array.isArray(entry.hexes) ? [...entry.hexes] : [];
        if (nextHexes.length >= MAX_IMAGE_COLORS) return entry;
        nextHexes.push("");
        return { ...entry, hexes: nextHexes, imageIndex: i };
      })
    );
  };
  const removeColorSlot = (index) => {
    setImageColorAssignments((prev) =>
      (Array.isArray(prev) ? prev : []).map((entry, i) => {
        if (i !== index) return entry;
        const nextHexes = Array.isArray(entry.hexes) ? [...entry.hexes] : [];
        if (nextHexes.length <= 1) return entry;
        nextHexes.pop();
        return { ...entry, hexes: nextHexes, imageIndex: i };
      })
    );
  };

  // Keep preview URLs in sync
  useEffect(() => {
    if (!isStoreContext) return;
    const revokable = [];
    const next = (storeImages || []).map((img) => {
      if (img instanceof File) {
        const u = URL.createObjectURL(img);
        revokable.push(u);
        return u;
      }
      return img?.url || img || "";
    });
    setStoreImagePreviewUrls(next);
    return () => revokable.forEach((u) => URL.revokeObjectURL(u));
  }, [isStoreContext, storeImages]);

  // When gallery changes (reorder/remove/add), keep color assignments aligned by image identity.
  useEffect(() => {
    if (!isStoreContext) return;
    setImageColorAssignments((prev) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const prevKeys = prevList.map((_, i) => imageKey(storeImages?.[i]));
      const prevMap = new Map();
      prevKeys.forEach((k, i) => {
        if (!k) return;
        prevMap.set(k, prevList[i]);
      });
      const nextAssignments = (Array.isArray(storeImages) ? storeImages : []).map((img, index) => {
        const k = imageKey(img);
        const saved = k ? prevMap.get(k) : null;
        return {
          name: saved?.name || "",
          hexes: Array.isArray(saved?.hexes) ? saved.hexes : [],
          imageIndex: index,
        };
      });
      return nextAssignments;
    });
  }, [isStoreContext, storeImages]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateProduct = async (e) => {
    e.preventDefault();

    if (isStoreContext && !String(categoryId || "").trim()) {
      toast.error("Please choose a store category.");
      return;
    }

    let discountPct = 0;
    if (isStoreContext) {
      const raw = String(discountPercentage ?? "").trim();
      if (raw !== "") {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0 || n > 100) {
          return toast.error("Discount must be between 0 and 100.");
        }
        discountPct = Math.round(n);
      }
    }

    const productData = new FormData();
    productData.append("name", name);
    productData.append("arName", arName);
    productData.append("category", category);
    productData.append("arCategory", arCategory);
    productData.append("price", price || "0");
    productData.append("alt", alt);
    productData.append("arAlt", arAlt);
    if (isStoreContext) {
      // Images: allow add/remove/reorder/edit crop by re-uploading full gallery when needed.
      const anyFile = Array.isArray(storeImages) && storeImages.some((img) => img instanceof File);
      const urlsNow = (Array.isArray(storeImages) ? storeImages : [])
        .map((img) => (img instanceof File ? "" : img?.url || img || ""))
        .filter(Boolean);
      const lengthChanged = urlsNow.length !== originalStoreImageUrls.length;
      const orderChanged =
        urlsNow.length === originalStoreImageUrls.length &&
        urlsNow.some((u, i) => String(u) !== String(originalStoreImageUrls[i]));

      if (anyFile || lengthChanged) {
        const files = await getImageFilesForUpload(storeImages);
        files.forEach((f) => productData.append("images", f));
      } else if (orderChanged) {
        const order = urlsNow.map((u) => originalStoreImageUrls.findIndex((x) => String(x) === String(u)));
        if (order.every((n) => Number.isInteger(n) && n >= 0)) {
          productData.append("imagesOrder", JSON.stringify(order));
        }
      }

      // Colors payload (per image)
      const colorsPayload = enableColors
        ? (colorMode === "single"
            ? [
                {
                  name: (imageColorAssignments?.[0]?.name || "").trim() || "Color",
                  hexes: (Array.isArray(imageColorAssignments?.[0]?.hexes) ? imageColorAssignments[0].hexes : [])
                    .map((h) => String(h || "").trim())
                    .filter(Boolean)
                    .slice(0, 4),
                  hex: String(
                    (Array.isArray(imageColorAssignments?.[0]?.hexes) ? imageColorAssignments[0].hexes[0] : "") || ""
                  ).trim(),
                  imageIndex: 0,
                },
              ].filter((entry) => entry.name || entry.hex || (entry.hexes && entry.hexes.length > 0))
            : (Array.isArray(imageColorAssignments) ? imageColorAssignments : [])
                .map((entry, index) => ({
                  name: (entry?.name || "").trim() || `Color ${index + 1}`,
                  hexes: (Array.isArray(entry?.hexes) ? entry.hexes : [])
                    .map((h) => String(h || "").trim())
                    .filter(Boolean)
                    .slice(0, 4),
                  hex: String((Array.isArray(entry?.hexes) ? entry.hexes[0] : "") || "").trim(),
                  imageIndex: index,
                }))
                .filter((entry) => entry.name || entry.hex || (entry.hexes && entry.hexes.length > 0)))
        : [];
      productData.append("colors", JSON.stringify(colorsPayload));
      productData.append("colorMode", colorMode);

      productData.append("discount_percentage", String(discountPct));
      productData.append("stock_quantity", String(stockQuantity ?? "0"));
      productData.append("min_delivery_days", String(minDeliveryDays ?? "5"));
      productData.append("max_delivery_days", String(maxDeliveryDays ?? "7"));
      productData.append("description", description);
      productData.append("arDescription", arDescription);
      productData.append("categoryId", String(categoryId || "").trim());
      if (sizeChart) productData.append("sizeChart", JSON.stringify(sizeChart));
      else productData.append("sizeChart", "");
      if (curtainPricing) productData.append("curtainPricing", JSON.stringify(curtainPricing));
      else productData.append("curtainPricing", "");
    } else {
      if (image instanceof File) {
        productData.append("image", image);
      }
    }

    try {
      setLoading(true);
      await toast.promise(
        axios.put(`${productsEndpoint}/${id}`, productData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }),
        {
          pending: "Updating product...",
          success: "Product updated successfully!",
        }
      );
      navigate(redirectPath);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="addProject">
      <form className="container" onSubmit={handleUpdateProduct}>
        <div className="title">
          <div className="text">
            <p>Edit Product</p>
          </div>
          <button type="submit" disabled={loading}>
            <UilCheck size={22} />
            Update Product
          </button>
        </div>
        <div className="form">
          <div className="inputs">
            <h3>General Information</h3>
            <div className="input">
              <label>
                Name <span>*</span>
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Product name"
              />
            </div>
            <div className="input ar-input">
              <label>
                الاسم <span>*</span>
              </label>
              <input
                value={arName}
                onChange={(e) => setArName(e.target.value)}
                type="text"
                placeholder="أعط منتجك اسمًا قصيرًا وواضحًا."
              />
            </div>
            <hr />
            {isStoreContext && productSku ? (
              <div className="input">
                <label>Product serial</label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <code
                    style={{
                      fontFamily: "ui-monospace, Consolas, monospace",
                      fontSize: "1.05rem",
                      letterSpacing: "0.06em",
                      padding: "8px 14px",
                      background: "rgba(154, 116, 98, 0.1)",
                      borderRadius: "8px",
                    }}
                  >
                    {productSku}
                  </code>
                  <button
                    type="button"
                    className="store-products-export-btn store-products-export-btn--secondary"
                    style={{ padding: "6px 12px", fontSize: "0.85rem" }}
                    onClick={() => {
                      navigator.clipboard
                        .writeText(productSku)
                        .then(() => toast.success("Serial copied."))
                        .catch(() => toast.error("Could not copy."));
                    }}
                  >
                    Copy
                  </button>
                </div>
                <p style={{ marginTop: "6px", fontSize: "0.85rem", color: "#8a7a6d" }}>
                  Assigned automatically at creation and cannot be changed.
                </p>
              </div>
            ) : null}
            <div className="input">
              <label>
                Category <span>*</span>
              </label>
              {isStoreContext ? (
                <select
                  value={categoryId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    setCategoryId(nextId);
                    const picked = storeCategories.find((c) => String(c._id) === String(nextId));
                    if (picked) {
                      setCategory(picked.label || "");
                      setArCategory(picked.arLabel || picked.label || "");
                    } else {
                      setCategory("");
                      setArCategory("");
                    }
                  }}
                >
                  <option value="" hidden>
                    Choose category
                  </option>
                  {storeCategories.map((c) => {
                    const indent = "—".repeat(Math.min(6, c.__depth || 0));
                    const en = c.label || "";
                    const ar = c.arLabel || "";
                    return (
                      <option key={c._id} value={c._id}>
                        {indent} {en} {ar ? `— ${ar}` : ""}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="" hidden>
                    Choose category
                  </option>
                  {categoryOptions.map((option) => (
                    <option key={option.en} value={option.en}>
                      {option.en}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {!isStoreContext ? (
              <div className="input ar-input">
                <label>
                  الفئة <span>*</span>
                </label>
                <select value={arCategory} onChange={(e) => setArCategory(e.target.value)}>
                  <option value="" hidden>
                    اختر فئة المنتج الخاص بك.
                  </option>
                  <option value="" disabled>
                    الفئات
                  </option>
                  {categoryOptions.map((option) => (
                    <option key={option.ar} value={option.ar}>
                      {option.ar}
                    </option>
                  ))}
                  {arCategory &&
                    !categoryOptions.some((option) => option.ar === arCategory) && (
                      <option value={arCategory}>{arCategory}</option>
                    )}
                </select>
              </div>
            ) : null}
            <div className="input">
              <label>Price (SAR)</label>
              <input
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter product price"
              />
            </div>
            {isStoreContext ? (
              <>
                <div className="input">
                  <label>Stock quantity</label>
                  <input
                    value={stockQuantity}
                    onChange={(e) => setStockQuantity(e.target.value)}
                    type="number"
                    min="0"
                    step="1"
                  />
                </div>
                <div className="input">
                  <label>Min delivery (days)</label>
                  <input
                    value={minDeliveryDays}
                    onChange={(e) => setMinDeliveryDays(e.target.value)}
                    type="number"
                    min="0"
                    step="1"
                  />
                </div>
                <div className="input">
                  <label>Max delivery (days)</label>
                  <input
                    value={maxDeliveryDays}
                    onChange={(e) => setMaxDeliveryDays(e.target.value)}
                    type="number"
                    min="0"
                    step="1"
                  />
                </div>
                <div className="input">
                  <label>Discount (%)</label>
                  <input
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="0"
                  />
                </div>
                <div className="input">
                  <label>Product description (English)</label>
                  <div
                    className="store-desc-tools"
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 10,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <button
                      type="button"
                      title="Bold"
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.12)",
                        background: "#fff",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleMarkdownWrap(descEnRef, setDescription, "**")}
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      title="Italic"
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.12)",
                        background: "#fff",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleMarkdownWrap(descEnRef, setDescription, "_")}
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      title="Bullets"
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.12)",
                        background: "#fff",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleMarkdownBullets(descEnRef, setDescription)}
                    >
                      •
                    </button>
                  </div>
                  <textarea
                    ref={descEnRef}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    placeholder="Details for the store product page and quick view."
                  />
                </div>
                <div className="input ar-input">
                  <label>وصف المنتج (عربي)</label>
                  <div
                    className="store-desc-tools"
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 10,
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <button
                      type="button"
                      title="Bold"
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.12)",
                        background: "#fff",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleMarkdownWrap(descArRef, setArDescription, "**")}
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      title="Italic"
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.12)",
                        background: "#fff",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleMarkdownWrap(descArRef, setArDescription, "_")}
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      title="Points"
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: "1px solid rgba(0,0,0,0.12)",
                        background: "#fff",
                        fontWeight: 900,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleMarkdownBullets(descArRef, setArDescription)}
                    >
                      •
                    </button>
                  </div>
                  <textarea
                    ref={descArRef}
                    value={arDescription}
                    onChange={(e) => setArDescription(e.target.value)}
                    rows={5}
                    placeholder="الوصف عند عرض المتجر بالعربية."
                    dir="rtl"
                  />
                </div>
                <SizeChartEditor value={sizeChart} onChange={setSizeChart} />
                {isCurtainsRootSelected ? (
                  <div className="input" style={{ marginTop: 12 }}>
                    <label>Curtains pricing (base + add-ons)</label>
                    <p style={{ margin: "6px 0 10px", opacity: 0.75 }}>
                      Set the minimum base price (100×150 + Plastic), then add-ons for other sizes/options. Discount (if set) applies to the total.
                    </p>
                    <div style={{ display: "grid", gap: 10 }}>
                      <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={curtainPricing?.enabled !== false}
                          onChange={(e) =>
                            setCurtainPricing((prev) => ({ ...(prev || {}), enabled: e.target.checked }))
                          }
                        />
                        Enable curtains dynamic pricing
                      </label>

                      <div style={{ display: "grid", gap: 8 }}>
                        <strong>Base minimum price (SAR)</strong>
                        <label style={{ display: "grid", gap: 4, maxWidth: 260 }}>
                          <span>100 cm × 150 cm + Plastic</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={curtainPricing?.basePrice ?? 0}
                            onChange={(e) =>
                              setCurtainPricing((prev) => ({
                                ...(prev || {}),
                                basePrice: Number(e.target.value),
                              }))
                            }
                          />
                        </label>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        <strong>Width add-ons (SAR)</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                          {[100, 125, 150, 175, 200, 225].map((w) => (
                            <label key={w} style={{ display: "grid", gap: 4 }}>
                              <span>{w} cm</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={curtainPricing?.widthAddons?.[w] ?? 0}
                                disabled={w === 100}
                                onChange={(e) =>
                                  setCurtainPricing((prev) => ({
                                    ...(prev || {}),
                                    widthAddons: { ...(prev?.widthAddons || {}), [w]: Number(e.target.value) },
                                  }))
                                }
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        <strong>Height add-ons (SAR)</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                          {[150, 175, 200, 225, 250, 275].map((h) => (
                            <label key={h} style={{ display: "grid", gap: 4 }}>
                              <span>{h} cm</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={curtainPricing?.heightAddons?.[h] ?? 0}
                                disabled={h === 150}
                                onChange={(e) =>
                                  setCurtainPricing((prev) => ({
                                    ...(prev || {}),
                                    heightAddons: { ...(prev?.heightAddons || {}), [h]: Number(e.target.value) },
                                  }))
                                }
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        <strong>Width/height adjustment add-ons (SAR)</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                          {[
                            ["none", "No adjustment"],
                            ["+5", "Add +5 cm"],
                            ["+10", "Add +10 cm"],
                            ["-5", "Remove -5 cm"],
                            ["-10", "Remove -10 cm"],
                          ].map(([k, label]) => (
                            <label key={k} style={{ display: "grid", gap: 4 }}>
                              <span>{label}</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={curtainPricing?.adjustmentAddons?.[k] ?? 0}
                                disabled={k === "none"}
                                onChange={(e) =>
                                  setCurtainPricing((prev) => ({
                                    ...(prev || {}),
                                    adjustmentAddons: { ...(prev?.adjustmentAddons || {}), [k]: Number(e.target.value) },
                                  }))
                                }
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        <strong>Chain type add-ons (SAR)</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                          {[
                            ["metal", "Metal"],
                            ["plastic", "Plastic"],
                          ].map(([k, label]) => (
                            <label key={k} style={{ display: "grid", gap: 4 }}>
                              <span>{label}</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={curtainPricing?.chainTypeAddons?.[k] ?? 0}
                                disabled={k === "plastic"}
                                onChange={(e) =>
                                  setCurtainPricing((prev) => ({
                                    ...(prev || {}),
                                    chainTypeAddons: { ...(prev?.chainTypeAddons || {}), [k]: Number(e.target.value) },
                                  }))
                                }
                              />
                            </label>
                          ))}
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: 6,
                          padding: 12,
                          borderRadius: 14,
                          border: "1px solid rgba(0,0,0,0.08)",
                          background: "rgba(255,255,255,0.75)",
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <strong>Live pricing preview</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
                          <label style={{ display: "grid", gap: 4 }}>
                            <span>Sample width</span>
                            <select
                              value={curtainSample.width}
                              onChange={(e) => setCurtainSample((p) => ({ ...p, width: Number(e.target.value) }))}
                            >
                              {[100, 125, 150, 175, 200, 225].map((n) => (
                                <option key={n} value={n}>
                                  {n} cm
                                </option>
                              ))}
                            </select>
                          </label>
                          <label style={{ display: "grid", gap: 4 }}>
                            <span>Sample height</span>
                            <select
                              value={curtainSample.height}
                              onChange={(e) => setCurtainSample((p) => ({ ...p, height: Number(e.target.value) }))}
                            >
                              {[150, 175, 200, 225, 250, 275].map((n) => (
                                <option key={n} value={n}>
                                  {n} cm
                                </option>
                              ))}
                            </select>
                          </label>
                          <label style={{ display: "grid", gap: 4 }}>
                            <span>Sample chain</span>
                            <select
                              value={curtainSample.chain}
                              onChange={(e) => setCurtainSample((p) => ({ ...p, chain: e.target.value }))}
                            >
                              <option value="plastic">Plastic</option>
                              <option value="metal">Metal</option>
                            </select>
                          </label>
                          <label style={{ display: "grid", gap: 4 }}>
                            <span>Width adjustment</span>
                            <select
                              value={curtainSample.wAdj}
                              onChange={(e) => setCurtainSample((p) => ({ ...p, wAdj: e.target.value }))}
                            >
                              <option value="none">No adjustment</option>
                              <option value="+5">Add +5 cm</option>
                              <option value="+10">Add +10 cm</option>
                              <option value="-5">Remove -5 cm</option>
                              <option value="-10">Remove -10 cm</option>
                            </select>
                          </label>
                          <label style={{ display: "grid", gap: 4 }}>
                            <span>Height adjustment</span>
                            <select
                              value={curtainSample.hAdj}
                              onChange={(e) => setCurtainSample((p) => ({ ...p, hAdj: e.target.value }))}
                            >
                              <option value="none">No adjustment</option>
                              <option value="+5">Add +5 cm</option>
                              <option value="+10">Add +10 cm</option>
                              <option value="-5">Remove -5 cm</option>
                              <option value="-10">Remove -10 cm</option>
                            </select>
                          </label>
                        </div>

                        {curtainPricingPreview ? (
                          <div
                            style={{
                              padding: "10px 12px",
                              borderRadius: 12,
                              background: "rgba(154,116,98,0.10)",
                              border: "1px solid rgba(154,116,98,0.18)",
                              fontWeight: 900,
                            }}
                          >
                            Base {curtainPricingPreview.base} + Width {curtainPricingPreview.widthAddon} + Height{" "}
                            {curtainPricingPreview.heightAddon} + Chain {curtainPricingPreview.chainAddon} + Adjustments{" "}
                            {curtainPricingPreview.wAdjAddon + curtainPricingPreview.hAdjAddon} ={" "}
                            {curtainPricingPreview.total} SAR
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="image">
            <h3>{isStoreContext ? "Upload images" : "Product Image"}</h3>
            {isStoreContext ? (
              <>
                <ProductImageUpload
                  images={storeImages}
                  onChange={(nextImages) => {
                    // Re-align color assignments by key before state update.
                    const prevMap = new Map(
                      (Array.isArray(storeImages) ? storeImages : []).map((img, i) => [
                        imageKey(img),
                        imageColorAssignments?.[i] || null,
                      ])
                    );
                    setStoreImages(nextImages);
                    setImageColorAssignments(
                      (Array.isArray(nextImages) ? nextImages : []).map((img, idx) => {
                        const saved = prevMap.get(imageKey(img));
                        return {
                          name: saved?.name || "",
                          hexes: Array.isArray(saved?.hexes) ? saved.hexes : [],
                          imageIndex: idx,
                        };
                      })
                    );
                  }}
                  error={""}
                  disabled={loading}
                />

                {storeImages.length > 0 ? (
                  <div className="store-colors-setup">
                    <label className="store-colors-toggle">
                      <input
                        type="checkbox"
                        checked={enableColors}
                        onChange={(e) => setEnableColors(e.target.checked)}
                        disabled={loading}
                      />
                      <span>Enable Colors for this product</span>
                    </label>

                    {enableColors ? (
                      <div className="store-colors-list">
                        <label className="store-colors-toggle" style={{ marginBottom: 10 }}>
                          <input
                            type="checkbox"
                            checked={colorMode === "single"}
                            onChange={(e) => setColorMode(e.target.checked ? "single" : "perImage")}
                            disabled={loading}
                          />
                          <span>Same color for all images</span>
                        </label>

                        {(colorMode === "single" ? [storeImagePreviewUrls[0]] : storeImagePreviewUrls).map((previewUrl, index) => (
                          <div key={index} className="store-color-card">
                            <p className="store-color-card-title">
                              {colorMode === "single" ? "All images" : `Image ${index + 1}`}
                            </p>
                            <div className="store-color-card-preview">
                              <img
                                src={
                                  String(previewUrl || "").startsWith("blob:") ||
                                  String(previewUrl || "").startsWith("data:")
                                    ? previewUrl
                                    : resolveUrl(String(previewUrl || ""), apiBaseUrl)
                                }
                                alt=""
                              />
                            </div>
                            <div className="input">
                              <label>Color Name</label>
                              <input
                                type="text"
                                value={(colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.name || ""}
                                onChange={(e) =>
                                  setImageColorAssignments((prev) =>
                                    (Array.isArray(prev) ? prev : []).map((entry, i) =>
                                      i === (colorMode === "single" ? 0 : index)
                                        ? { ...entry, name: e.target.value, imageIndex: i }
                                        : entry
                                    )
                                  )
                                }
                                placeholder={colorMode === "single" ? "e.g. Color" : `e.g. Color ${index + 1}`}
                                disabled={loading}
                              />
                            </div>
                            <div className="store-color-code-row">
                              <div className="input">
                                <label>Color 1 (Hex)</label>
                                <input
                                  type="text"
                                  value={(colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes?.[0] || ""}
                                  onChange={(e) => handleColorHexChange(colorMode === "single" ? 0 : index, 0, e.target.value)}
                                  placeholder="#000000"
                                  disabled={loading}
                                />
                              </div>
                              <input
                                type="color"
                                value={
                                  /^#([0-9a-f]{6})$/i.test((colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes?.[0] || "")
                                    ? (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index]).hexes[0]
                                    : "#000000"
                                }
                                onChange={(e) => handleColorHexChange(colorMode === "single" ? 0 : index, 0, e.target.value)}
                                className="store-color-native-picker"
                                aria-label={`Select color for image ${index + 1}`}
                                disabled={loading}
                              />
                            </div>
                            {(((colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes) || []).slice(1).map((_, slotIdx) => {
                              const slot = slotIdx + 1;
                              const current = (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes?.[slot] || "";
                              const fallback = slot === 1 ? "#ffffff" : "#000000";
                              return (
                                <div key={`${index}-slot-${slot}`} className="store-color-code-row">
                                  <div className="input">
                                    <label>{`Color ${slot + 1} (Hex)`}</label>
                                    <input
                                      type="text"
                                      value={current}
                                      onChange={(e) => handleColorHexChange(colorMode === "single" ? 0 : index, slot, e.target.value)}
                                      placeholder="#FFFFFF (optional)"
                                      disabled={loading}
                                    />
                                  </div>
                                  <input
                                    type="color"
                                    value={/^#([0-9a-f]{6})$/i.test(current) ? current : fallback}
                                    onChange={(e) => handleColorHexChange(colorMode === "single" ? 0 : index, slot, e.target.value)}
                                    className="store-color-native-picker"
                                    aria-label={`Select color ${slot + 1} for image ${index + 1}`}
                                    disabled={loading}
                                  />
                                </div>
                              );
                            })}

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                              <button
                                type="button"
                                className="store-color-extract-btn"
                                onClick={() => openColorPicker(colorMode === "single" ? 0 : index, 0)}
                                disabled={loading}
                              >
                                Extract color 1 from image
                              </button>
                              {(() => {
                                const target = colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index];
                                const slots = Array.isArray(target?.hexes) ? target.hexes.length : 0;
                                return slots >= 2 ? (
                                  <button
                                    type="button"
                                    className="store-color-extract-btn"
                                    onClick={() => openColorPicker(colorMode === "single" ? 0 : index, 1)}
                                    disabled={loading}
                                  >
                                    Extract color 2 from image
                                  </button>
                                ) : null;
                              })()}
                              <button
                                type="button"
                                className="store-color-extract-btn"
                                onClick={() => addColorSlot(colorMode === "single" ? 0 : index)}
                                disabled={
                                  loading ||
                                  (Array.isArray((colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes)
                                    ? (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index]).hexes.length >= MAX_IMAGE_COLORS
                                    : true)
                                }
                              >
                                Add color
                              </button>
                              <button
                                type="button"
                                className="store-color-extract-btn"
                                onClick={() => removeColorSlot(colorMode === "single" ? 0 : index)}
                                disabled={
                                  loading ||
                                  !Array.isArray((colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index])?.hexes) ||
                                  (colorMode === "single" ? imageColorAssignments[0] : imageColorAssignments[index]).hexes.length <= 1
                                }
                              >
                                Remove color
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                {image ? (
                  <div className="imgPreview">
                    <img
                      src={image instanceof File ? URL.createObjectURL(image) : image}
                      alt={alt || "Product Image"}
                    />
                    <button className="remove-btn" onClick={handleRemoveImage} type="button">
                      <i className="uil uil-times"></i>
                    </button>
                  </div>
                ) : (
                  <div
                    className="imgInput"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleFileUpload(e.dataTransfer.files[0]);
                    }}
                  >
                    <UilImageUpload size={40} />
                    <p>Drop your image here, or</p>
                    <div className="btn">
                      <button type="button">Click here</button>
                      <input type="file" onChange={(e) => handleFileUpload(e.target.files[0])} accept="image/*" />
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="input">
              <label>
                Image Alt Text <span>*</span>
              </label>
              <input
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                type="text"
                placeholder="Alt text for the image"
              />
            </div>
            <div className="input ar-input">
              <label>
                نص الصورة البديل <span>*</span>
              </label>
              <input
                value={arAlt}
                onChange={(e) => setArAlt(e.target.value)}
                type="text"
                placeholder="أعط صورة مشروعك نصًا بديلًا قصيرًا."
              />
            </div>
          </div>
        </div>
      </form>
      {colorPickerState.open && (
        <PixelColorPicker
          imageUrl={colorPickerState.imageUrl}
          onPickColor={(hex) => {
            if (typeof colorPickerState.imageIndex === "number") {
              handleColorHexChange(colorPickerState.imageIndex, colorPickerState.hexSlot || 0, hex);
            }
            setColorPickerState({ open: false, imageUrl: "", imageIndex: null, hexSlot: 0 });
          }}
          onClose={() => setColorPickerState({ open: false, imageUrl: "", imageIndex: null, hexSlot: 0 })}
        />
      )}
    </main>
  );
};

export default EditProduct;
